import classNames from 'classnames';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import Sizes from '../utils/responsive/Sizes';
import isSize from '../utils/responsive/isSize';
import * as styles from './BannerEditor.module.scss';
import type { Banner } from './BannerModalContent';
import BannerModalContent from './BannerModalContent';
import Button from './Button';
import type { ImageEditorRef } from './ImageEditor';
import ImageEditor, {
  DragZoomInfoPill,
  ImageEditorPopOver,
} from './ImageEditor';
import ImageEditorDropZone from './ImageEditorDropZone';
import { BANNER_IMAGE_LIBRARY } from './imageLibraries';

// We multiply width and height by 2 to save higher resolution
// images that look good on retina displays.
const BANNER_HEIGHT = parseInt(styles.bannerHeight, 10) * 2;
const BANNER_WIDTH = parseInt(styles.bannerWidth, 10) * 2;

const BANNER_EDITOR_ACCEPT = [
  'image/apng',
  'image/avif',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
].reduce((acc, type) => ({ ...acc, [type]: [] }), {});

export interface BannerEditorRef {
  getBannerFile(): Promise<File | null>;
}

interface BannerEditorProps {
  currentBannerUrl?: string | null;
  state: 'viewing' | 'editing' | 'saving';
  onBannerChange(banner: Banner | null): void;
  banner: Banner | null;
}

const BannerEditor = React.forwardRef<BannerEditorRef, BannerEditorProps>(
  ({ currentBannerUrl, state, onBannerChange, banner }, ref) => {
    const [isPositioning, setIsPositioning] = useState(false);

    useEffect(() => {
      if (state !== 'editing' || banner == null) {
        setIsPositioning(false);
      }
    }, [currentBannerUrl, state, banner]);

    const imageButtonRef = useRef<HTMLButtonElement>(null);
    const imageEditorRef = useRef<ImageEditorRef>(null);

    useImperativeHandle(ref, () => ({
      async getBannerFile() {
        return imageEditorRef.current ? imageEditorRef.current.getFile() : null;
      },
    }));

    const [isBannerPopOverOpen, setIsBannerPopOverOpen] = useState(false);

    const handleBannerChange = (newBanner: Banner | null) => {
      if (newBanner) {
        setIsPositioning(true);
      }
      setIsBannerPopOverOpen(false);
      onBannerChange(newBanner);
    };

    const hasImage =
      state === 'viewing' ? currentBannerUrl != null : banner != null;

    return (
      <div
        className={classNames(styles.container, {
          [styles.isEditing]: state === 'editing',
          [styles.hasImage]: hasImage,
        })}
      >
        {banner ? (
          <Button
            className={styles.imageButton}
            ref={imageButtonRef}
            onClick={() => {
              if (state === 'editing' && !isPositioning) {
                setIsBannerPopOverOpen((isOpen) => !isOpen);
              }
            }}
            disabled={state === 'viewing'}
          >
            <ImageEditorDropZone
              disabled={state !== 'editing'}
              onDropImage={(image) => handleBannerChange({ image })}
              accept={BANNER_EDITOR_ACCEPT}
            >
              <ImageEditor
                ref={imageEditorRef}
                value={banner}
                src={currentBannerUrl ?? null}
                height={BANNER_HEIGHT}
                width={BANNER_WIDTH}
                positioning={isPositioning}
              />
            </ImageEditorDropZone>
          </Button>
        ) : (
          <Button
            className={styles.imageButton}
            onClick={() => setIsBannerPopOverOpen((isOpen) => !isOpen)}
          >
            <ImageEditorDropZone
              onDropImage={(image) => handleBannerChange({ image })}
              accept={BANNER_EDITOR_ACCEPT}
            >
              <div className={styles.emptyDropZoneInfo}>
                <div>Personalize with a banner image</div>
              </div>
            </ImageEditorDropZone>
          </Button>
        )}

        {state !== 'viewing' && (
          <div className={styles.controlsContainer}>
            {isPositioning && isSize(Sizes.LG) ? <DragZoomInfoPill /> : <div />}

            <div className={styles.controls}>
              <ImageEditorPopOver
                open={isBannerPopOverOpen}
                onOpenChange={setIsBannerPopOverOpen}
                placement="bottom-end"
                preventCloseOnClickOutside={(target) =>
                  imageButtonRef.current?.contains(target) ?? false
                }
                content={
                  <BannerModalContent
                    imageLibrary={BANNER_IMAGE_LIBRARY}
                    displayMode="full"
                    onRemove={() => handleBannerChange(null)}
                    onBannerChange={handleBannerChange}
                    uploaderAccept={BANNER_EDITOR_ACCEPT}
                  />
                }
              >
                <Button
                  variant="secondary"
                  onClick={() => setIsBannerPopOverOpen(true)}
                  disabled={state === 'saving'}
                  size="small"
                >
                  {hasImage ? 'Edit banner' : 'Add banner'}
                </Button>
              </ImageEditorPopOver>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default BannerEditor;
